/**
 * forecastDerivation.ts — DASH-FC1 pure derivation helpers.
 *
 * Two pure, synchronous helpers consumed by ForecastPage (rewrite):
 *
 *   1. `deriveBestWindow(tiers)` — argmax of `PerDayTier.score`, tiebreak
 *      earlier `date`, null on empty input.
 *
 *   2. `isNearDay(weather, dayDate, lat, lng)` — re-derives the engine NEAR/FAR
 *      predicate VALUE-IDENTICALLY to `conditions-quality-service.ts:409-422`.
 *      The screen-side copy exists so the page can branch on NEAR/FAR per row
 *      WITHOUT adding a `near` flag to `PerDayTier` (Reviewer ruling A2 —
 *      stays inside the engine-change fence; predicate verified value-identical).
 *
 * Parity comment: the predicate below is intentionally identical to the engine
 * block at `src/services/conditions-quality-service.ts:409-422`. Any divergence
 * in the NEAR/FAR predicate is a bug — the NEAR/FAR outcome must match for every
 * day when called with the same inputs.
 *
 * Scope fence (RULE-276): this file imports no service other than the shipped
 * engine helpers; no StorageAdapter, no Dexie, no Supabase.
 */

import {
  computeFavorabilityByPeriod,
} from '@/services/conditions-quality-service';
import { getSunTimes } from '@/services/celestial-service';
import type { PerDayTier } from '@/types/conditions';
import type { WeatherData } from '@/services/weather-service';

// ---------------------------------------------------------------------------
// deriveBestWindow
// ---------------------------------------------------------------------------

/**
 * Return the `PerDayTier` with the highest continuous `score`. On a tie,
 * the day with the earlier `date` string (ISO 8601 lexicographic order, which
 * preserves chronological order) is returned. Returns `null` when `tiers` is
 * empty.
 *
 * Derive from `score`, not from the coarse `tier` bucket — two "Prime" days
 * can have meaningfully different scores.
 */
export function deriveBestWindow(tiers: PerDayTier[]): PerDayTier | null {
  if (tiers.length === 0) return null;

  // SAFETY: tiers is non-empty so reduce will always have a starting value.
  return tiers.reduce<PerDayTier>((best, current) => {
    if (current.score > best.score) return current;
    // Tiebreak: earlier date wins (lexicographic ISO 8601 is chronological).
    if (current.score === best.score && current.date < best.date) return current;
    return best;
  });
}

// ---------------------------------------------------------------------------
// isNearDay — engine-parity predicate
// ---------------------------------------------------------------------------

/**
 * Return `true` iff the engine would classify `dayDate` as a NEAR day for
 * the given `weather` + coordinates.
 *
 * Parity rule: the predicate is VALUE-IDENTICAL to
 * `conditions-quality-service.ts:409-422` (DASH-T4 engine). Every line below
 * maps directly to the engine block. Do NOT add index-based shortcuts here —
 * a day is NEAR iff the hourly horizon reaches its civil dusk, regardless of
 * its position in the daily array.
 *
 * @param weather  - Full WeatherData object (hourly + daily both consulted).
 * @param dayDate  - The `Date` for this day (constructed from `daily.dt * 1000`
 *                   in the engine — callers must pass the same object).
 * @param lat      - Property latitude.
 * @param lng      - Property longitude.
 */
export function isNearDay(
  weather: WeatherData,
  dayDate: Date,
  lat: number,
  lng: number,
): boolean {
  // Parity line 1: per-step series — empty when no hourly data (guard is inside
  // computeFavorabilityByPeriod; empty hourly → []).
  const series = computeFavorabilityByPeriod(weather, dayDate, lat, lng);

  // Parity line 2: civil dusk for THIS day (dt * 1000 is already done by the
  // caller constructing `dayDate`; getSunTimes takes a Date, not a unix int).
  const civilDusk = getSunTimes(dayDate, lat, lng).civilDusk;

  // Parity line 3: last hourly timestamp in ms. Empty hourly → -Infinity
  // (explicit no-NEAR fallback — a sentinel that is always < any real dusk time).
  const lastHourlyMs =
    weather.hourly.length > 0
      ? weather.hourly[weather.hourly.length - 1].dt * 1000
      : -Infinity;

  // Parity line 4: the NEAR predicate — value-identical to engine line 422.
  return series.length > 0 && lastHourlyMs >= civilDusk.getTime();
}
